'use client';

import {
  Button as MuiButton,
  ButtonProps as MuiButtonProps,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

import { Arrow } from '../../icons';

export interface ButtonProps extends Omit<MuiButtonProps, 'href' | 'variant'> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'text';
  href?: string;
  external?: boolean;
  size?: 'small' | 'medium' | 'large';
  fullWidth?: boolean;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'start' | 'end';
  showArrow?: boolean;
}

// Theme colors from frontend
const theme = {
  colors: {
    primary: '#EB722E',
    primaryDark: '#D65A1A',
    secondary: '#001A44',
    secondaryDark: '#000d22',
    disabled: '#ccc',
  },
};

// Styled MUI Button with animation
const AnimatedButton = styled(MuiButton)(({ theme: muiTheme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: '5px',
  textTransform: 'uppercase',
  fontWeight: 400,
  boxShadow: 'none',
  // transition: 'all 0.2s ease',

  // Arrow animation
  '& .MuiSvgIcon-root': {
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    marginLeft: muiTheme.spacing(1),
    transform: 'translateX(-8px)', // Start more left
    opacity: 0.7,
    fontSize: '1.5rem', // Make arrow slightly bigger
  },

  '&:hover': {
    paddingRight: muiTheme.spacing(5), // More padding for longer arrow movement
    '& .MuiSvgIcon-root': {
      transform: 'translateX(8px) scaleX(1.2)', // Move further right and stretch
      opacity: 1,
    },
  },

  // Smoother transition for padding
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  padding: muiTheme.spacing(1.5, 3), // Base padding

  '&:active': {
    transform: 'translateY(0)',
  },

  '&:focus': {
    outline: 'none',
  },

  // Size variants
  '&.small': {
    padding: '8px 16px',
    fontSize: '14px',
    minHeight: '36px',
  },
  '&.medium': {
    padding: '12px 24px',
    fontSize: '16px',
    minHeight: '44px',
  },
  '&.large': {
    padding: '16px 32px',
    fontSize: '18px',
    minHeight: '52px',
  },

  // Color variants with exact colors from frontend
  '&.primary': {
    backgroundColor: theme.colors.primary,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.colors.primaryDark,
      paddingRight: muiTheme.spacing(3),
    },
    '&:disabled': {
      backgroundColor: theme.colors.disabled,
    },
  },
  '&.secondary': {
    backgroundColor: theme.colors.secondary,
    color: 'white',
    '&:hover': {
      backgroundColor: theme.colors.secondaryDark,
      paddingRight: muiTheme.spacing(3),
    },
    '&:disabled': {
      backgroundColor: theme.colors.disabled,
    },
  },
  '&.outline': {
    backgroundColor: 'transparent',
    color: theme.colors.primary,
    border: `2px solid ${theme.colors.primary}`,
    '&:hover': {
      backgroundColor: theme.colors.primary,
      color: 'white',
      paddingRight: muiTheme.spacing(3),
    },
    '&:disabled': {
      borderColor: theme.colors.disabled,
      color: theme.colors.disabled,
    },
  },
  '&.text': {
    backgroundColor: 'transparent',
    color: theme.colors.primary,
    '&:hover': {
      backgroundColor: 'rgba(235, 114, 46, 0.1)',
      paddingRight: muiTheme.spacing(3),
    },
    '&:disabled': {
      color: theme.colors.disabled,
    },
  },
  '&.ghost': {
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(235, 114, 46, 0.1)',
      paddingRight: muiTheme.spacing(3),
    },
  },
}));

export const Button: React.FC<ButtonProps> = ({
  children,
  href,
  external = false,
  variant = 'primary',
  size = 'medium',
  fullWidth = false,
  loading = false,
  icon,
  iconPosition = 'end',
  showArrow = true,
  disabled,
  className,
  onClick,
  ...props
}) => {
  // Handle link behavior
  const linkProps = href
    ? {
        href,
        target: external ? '_blank' : undefined,
        rel: external ? 'noopener noreferrer' : undefined,
      }
    : {};

  // Map custom variants to MUI variants
  const getMuiVariant = () => {
    switch (variant) {
      case 'primary':
      case 'secondary':
        return 'contained';
      case 'outline':
        return 'outlined';
      case 'text':
      case 'ghost':
        return 'text';
      default:
        return 'contained';
    }
  };

  // Icon positioning
  const getIcon = () => {
    if (loading) return null;
    if (icon) {
      return {
        [iconPosition === 'start' ? 'startIcon' : 'endIcon']: icon,
      };
    }
    if (showArrow && !icon) {
      return {
        endIcon: <Arrow />,
      };
    }
    return {};
  };

  return (
    <AnimatedButton
      variant={getMuiVariant()}
      className={`${variant} ${size} ${className || ''}`}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      onClick={onClick}
      {...linkProps}
      {...getIcon()}
      {...props}
    >
      {loading ? 'Loading...' : children}
    </AnimatedButton>
  );
};

export default Button;
